import React from "react";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  faCheck,
  faXmark,
  faChevronCircleDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as f from "../../../utils/formatter";
import * as d from "shared/src/date.mjs";
import { TabContext, TabPanel } from "@mui/lab";
import { styled, useTheme } from "@mui/system";
import Divider from "@mui/material/Divider";
import { TREATMENT_STATE_INACTIVE } from "shared/src/company-tag.mjs";

function BooleanValue({ value }) {
  return value ? (
    <FontAwesomeIcon icon={faCheck} />
  ) : (
    <FontAwesomeIcon icon={faXmark} />
  );
}

function Info({ label, value }) {
  return (
    <Typography variant="body1" sx={{ width: "100%" }}>
      <strong>{label}</strong>
      <br />
      {value}
    </Typography>
  );
}

function getLabelsProps(activeTreatment, treatment, theme) {
  if (activeTreatment === treatment.id) {
    return {
      title: { color: "textPrimary" },
      subtitle: { color: theme.palette.primary._states.outlinedColor },
    };
  }

  if (treatment.state != TREATMENT_STATE_INACTIVE) {
    return {
      title: { color: theme.palette.primary._states.outlinedColor },
      subtitle: { color: "#857C86" },
    };
  }

  return {
    title: {},
    subtitle: {},
  };
}

export function TreatmentCard({ client, displayMinimalInfo = false }) {
  const [activeTab, setActiveTab] = React.useState(client.treatments[0]?.id);
  const theme = useTheme();

  return (
    <Card>
      <CardContent>
        {client.treatments && client.treatments.length > 0 ? (
          <>
            <Tabs value={activeTab} onChange={(_, tab) => setActiveTab(tab)}>
              {client.treatments.map((t) => {
                const labelsProps = getLabelsProps(activeTab, t, theme);

                return (
                  <Tab
                    key={t.id}
                    label={
                      <>
                        <Typography variant="body2" {...labelsProps.title}>
                          {t.product.name}
                        </Typography>
                        <Typography {...labelsProps.subtitle}>
                          ({t.state})
                        </Typography>
                      </>
                    }
                    value={t.id}
                  />
                );
              })}
            </Tabs>
            <TabContext value={activeTab}>
              {client.treatments.map((treatment) => (
                <TabPanel key={treatment.id} value={treatment.id}>
                  <Treatment
                    treatment={treatment}
                    displayMinimalInfo={displayMinimalInfo}
                  />
                </TabPanel>
              ))}
            </TabContext>
          </>
        ) : (
          <Typography>Žádné léčebné plány</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BraceTreatment({ treatment, displayMinimalInfo }) {
  return (
    <>
      <Stack direction="row" spacing={5}>
        <Stack
          spacing={3}
          direction={displayMinimalInfo ? "row" : "column"}
          sx={{ mb: 3 }}
        >
          <Info label="Typ léčby" value={f.value(treatment.type)} />
          <Info label="Podtyp léčby" value={f.value(treatment.subtype)} />
          <Info
            label="Virtual care"
            value={
              <>
                <BooleanValue value={treatment.virtualCareInfo.active} />
                {treatment.virtualCareInfo?.email != null && (
                  <>
                    <br />
                    <span style={{ whiteSpace: "nowrap" }}>
                      <strong>Email: </strong>
                      {treatment.virtualCareInfo?.email}
                    </span>
                  </>
                )}
              </>
            }
          />
        </Stack>
        {!displayMinimalInfo && (
          <>
            <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
              <Info
                label="Začátek léčby"
                value={f.value(f.date(treatment.start))}
              />
              <Info
                label="Délka léčby"
                value={f.value(
                  d.yearsDiff(
                    d.parseDateTime(treatment.start),
                    d.parseDateTime(treatment.end) ?? new Date(),
                  ),
                )}
              />
              <Info
                label="Datum expirace"
                value={f.value(f.date(treatment.expires_at))}
              />
            </Stack>

            <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
              <Info
                label="Problémová léčba"
                value={<BooleanValue value={treatment.problematic} />}
              />
              <Info
                label="Počet refinementů"
                value={f.value(treatment.refinements_count)}
              />
              <Info
                label="IPR"
                value={<span>{f.value(treatment.stripping_stage)}</span>}
              />
            </Stack>
          </>
        )}
      </Stack>
      {!displayMinimalInfo && (
        <Box sx={{ mt: 2 }}>
          <Info label="Info o léčbě" value={f.value(treatment.note)} />
        </Box>
      )}
      {treatment.plans.map((plan, i) => (
        <PlanInfo
          key={plan.id}
          plan={plan}
          order={treatment.plans.length - i}
          newest={treatment.plans.length - i === treatment.plans.length}
          displayMinimalInfo={displayMinimalInfo}
        />
      ))}
    </>
  );
}

function WhiteningTreatment({ treatment }) {
  return (
    <>
      <Stack direction="row" spacing={5}>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info
            label="Začátek léčby"
            value={f.value(f.date(treatment.start))}
          />
        </Stack>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info label="Výdej" value={f.value(treatment.dispensation)} />
          <Info label="Vyzvednutí" value={f.value(treatment.pickup)} />
        </Stack>
      </Stack>
    </>
  );
}

function SplintTreatment({ treatment }) {
  return (
    <>
      <Stack direction="row" spacing={5}>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info label="Typ léčby" value={f.value(treatment.type)} />
          <Info label="Info" value={f.value(treatment.note)} />
          <Info
            label="Začátek léčby"
            value={f.value(f.date(treatment.start))}
          />
        </Stack>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info label="Vyzvednutí" value={f.value(treatment.pickup)} />
        </Stack>
      </Stack>
    </>
  );
}

function RetainerTreatment({ treatment }) {
  return (
    <>
      <Stack direction="row" spacing={5}>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info
            label="Datum předání poslední dlahy"
            value={f.value(f.date(treatment.last_retainer_dispensation_at))}
          />
          <Info
            label="Zbývá plateb do další dlahy"
            value={f.value(treatment.next_retainer_payments)}
          />
          <Info
            label="Stav další dlahy"
            value={f.value(treatment.next_retainer_status)}
          />
        </Stack>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          <Info
            label="Datum poslední platby"
            value={f.value(f.date(treatment.last_retainer_at))}
          />
        </Stack>
      </Stack>
    </>
  );
}

const treatmentComponentByProductId = {
  "59b7da94-8ce7-4bd1-bdce-f981a72c421e": BraceTreatment,
  "a2e8cc90-2f57-4e7d-9e8d-31e7efed6425": WhiteningTreatment,
  "380858af-59e2-4963-a2e8-9a2d1be14af0": SplintTreatment,
  "e9d96301-6c48-4e0a-ae19-55bd3a7c4822": RetainerTreatment,
};

function Treatment({ treatment, displayMinimalInfo }) {
  const [expanded, setExpanded] = React.useState(
    treatment.state !== TREATMENT_STATE_INACTIVE,
  );

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const Component = treatmentComponentByProductId[treatment.product_id];
  if (Component == null) {
    return null;
  }

  return (
    <>
      <Typography
        variant="h3"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Box
          sx={{
            mb: 4,
          }}
        >
          Léčba - {treatment.state} - {f.value(f.date(treatment.end))}
        </Box>
        <Box>
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <FontAwesomeIcon icon={faChevronCircleDown} />
          </ExpandMore>
        </Box>
      </Typography>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Component
          treatment={treatment}
          displayMinimalInfo={displayMinimalInfo}
        />
      </Collapse>
    </>
  );
}

function PlanInfo({ plan, order, newest, displayMinimalInfo = false }) {
  const [expanded, setExpanded] = React.useState(newest);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Box key={plan.id} sx={{ mt: 2 }}>
      <Divider />
      <Typography
        variant="h3"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Box>{`Plán č. ${order} - ${plan.type} - ${plan.state}`}</Box>
        <Box>
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <FontAwesomeIcon icon={faChevronCircleDown} />
          </ExpandMore>
        </Box>
      </Typography>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={3} direction="column" sx={{ mb: 3 }}>
          {!displayMinimalInfo && (
            <Info label="Info o plánu" value={f.value(plan.note)} />
          )}
          <Stack direction="row">
            <Info
              label="Link na léčebný plán"
              value={
                plan.link.length > 0 && (
                  <>
                    <a href={plan.link} target="_blank" rel="noreferrer">
                      Link na léčebný plán (z CP app)
                    </a>
                  </>
                )
              }
            />
            <>
              <Info
                label="Poslední předaná dlaha"
                value={f.value(plan.retainerInfo?.last)}
              />
              <Info
                label="Frekvence výměny dlah (dnů)"
                value={f.value(plan.retainerInfo?.frequency)}
              />
            </>
          </Stack>

          <Info
            label="Tahy"
            value={f.value(plan.retainerInfo?.rubber_bands_note)}
          />
        </Stack>
      </Collapse>
    </Box>
  );
}

const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "expand",
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));
